import { defineComponent as _defineComponent } from 'vue'
import { Payment, PaymentType, User } from "@/CodegenTypes";
import { mutations, queries } from "@/apollo";
import { PAYMENT_TYPE_DISPLAY } from "@/constants";
import { customMoment } from "@/main";
import { formatDollar } from "@/helpers/formatDollar";
import { sumBy } from "lodash";
import { fixRounding } from "@/helpers/fixRounding";
import { errorMutationNotification } from "@/utils";
import { computed, ref, watch } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { addSuccessToast, primeVueConfirm } from "@/vue-service";
import { apolloClient } from "@/apollo";
import { useTranslation } from "@/store/translation";


export default /*#__PURE__*/_defineComponent({
  __name: 'PaymentStatementModal',
  props: {
    userId1: null,
    userId2: null,
    value: { type: Boolean }
  },
  emits: ["input", "reload"],
  setup(__props: any, { emit }) {

const props = __props as {
  userId1: User["userId"];
  userId2: User["userId"];
  value: boolean;
};



const {
  result: meResult,
  refetch: refetchMe,
  loading: loadingMe,
} = useQuery<{ me: User }>(queries.me, {}, { fetchPolicy: "cache-first" });
const {
  result: userResult,
  refetch: refetchUser,
  loading: loadingUser,
} = useQuery<{ user: User }>(
  queries.userWithPaymentHistory,
  {
    userId: computed(() => props.userId1),
    withUser: computed(() => props.userId2),
  },
  { fetchPolicy: "cache-and-network" }
);



const submitting = ref(false);

const thisMonthOnly = ref(true);

const viewerIsUpline = computed(() => {
  if (!meResult.value?.me) {
    return false;
  }
  if (meResult.value.me.roleName === "admin") {
    return true;
  }
  return userResult.value?.user.lineageIds.includes(meResult.value?.me.userId);
});

const { $t } = useTranslation();


const paymentTypes: PaymentType[] = [
  PaymentType.DdBet,
  PaymentType.DdOutsetClaim,
  PaymentType.DdPositionShare,
  PaymentType.DdStrikePayout,
  PaymentType.PaymentReceived,
  PaymentType.StrikePayout,
  PaymentType.TtBet,
  PaymentType.TtBetD1,
  PaymentType.TtBetD2,
  PaymentType.TtBetD3,
  PaymentType.TtBetD4,
  PaymentType.TtOutsetClaim,
  PaymentType.TtOutsetClaimD1,
  PaymentType.TtOutsetClaimD2,
  PaymentType.TtOutsetClaimD3,
  PaymentType.TtOutsetClaimD4,
  PaymentType.TtPositionShare,
  PaymentType.TtPositionShareD1,
  PaymentType.TtPositionShareD2,
  PaymentType.TtPositionShareD3,
  PaymentType.TtPositionShareD4,
  PaymentType.TtStrikePayout,
  PaymentType.TtStrikePayoutD1,
  PaymentType.TtStrikePayoutD2,
  PaymentType.TtStrikePayoutD3,
  PaymentType.TtStrikePayoutD4,
  PaymentType.OtherNegative,
  PaymentType.OtherPositive,
];

const relevantPaymentHistory = computed(() => {
  let total = 0;
  const paymentHistoryWithTotal = [
    ...(userResult.value?.user.paymentHistory || []),
  ]
    .sort((a, b) => {
      // release date first
      const releaseDateDiff = customMoment(a.releaseDate).diff(b.releaseDate);
      if (releaseDateDiff) {
        return releaseDateDiff;
      }

      // then created at
      const createdAtDiffSeconds = customMoment(a.createdAt).diff(
        b.createdAt,
        "seconds"
      );
      if (createdAtDiffSeconds > 1) {
        return createdAtDiffSeconds;
      }

      // finally by types
      return (
        paymentTypes.indexOf(a.paymentType) -
        paymentTypes.indexOf(b.paymentType)
      );
    })
    .map((payment) => {
      total += payment.amount;
      return {
        ...payment,
        total,
      };
    });

  if (thisMonthOnly.value) {
    return paymentHistoryWithTotal?.filter((h) => {
      return customMoment(h.createdAt).isSameOrAfter(
        customMoment().startOf("month")
      );
    });
  } else {
    return paymentHistoryWithTotal;
  }
});

const prevPaymentsSummary = computed(() => {
  const payments = userResult.value?.user.paymentHistory.filter((h) => {
    return customMoment(h.createdAt).isBefore(customMoment().startOf("month"));
  });

  return {
    amount: sumBy(payments, "amount"),
  };
});

const paymentsSummary = computed(() => {
  return {
    amount: sumBy(userResult.value?.user.paymentHistory, "amount"),
  };
});

function toggleModal(val: boolean) {
  emit("input", val);
}

function formatPaymentType(paymentType: Payment["paymentType"]) {
  if (!paymentType) {
    return "";
  }
  return PAYMENT_TYPE_DISPLAY[paymentType].display;
}

function formatDrawDate(dateStr?: string) {
  if (!dateStr) {
    return null;
  }
  return customMoment(dateStr).format("ddd, DD/MM/YYYY");
}
function formatDate(dateStr: string) {
  return customMoment(dateStr).format("ddd, DD/MM/YYYY hh:mm:ss A");
}

async function deletePayment(id: Payment["id"]) {
  const { isOk } = await primeVueConfirm({
    title: "Delete payment?",
    text: "This action cannot be undone.",
    icon: "question",
  });

  if (!isOk) {
    return;
  }
  try {
    submitting.value = true;
    await apolloClient.mutate({
      mutation: mutations.deletePayment,
      variables: {
        id,
      },
    });
    addSuccessToast("Payment deleted.");
    reloadAll();
    emit("reload");
  } catch (err) {
    errorMutationNotification(err);
  }
  submitting.value = false;
}

function reset() {
  submitting.value = false;
  thisMonthOnly.value = true;
}

function reloadAll() {
  refetchUser();
  refetchMe();
}

watch(
  () => props.value,
  () => {
    reset();
    reloadAll();
  }
);

return { __sfc: true,props, meResult, refetchMe, loadingMe, userResult, refetchUser, loadingUser, emit, submitting, thisMonthOnly, viewerIsUpline, $t, paymentTypes, relevantPaymentHistory, prevPaymentsSummary, paymentsSummary, toggleModal, formatPaymentType, formatDrawDate, formatDate, deletePayment, reset, reloadAll, formatDollar, fixRounding }
}

})